import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PulseSection from '../components/PulseSection';
import { Alert, Button, Container } from '../components/site/ui';
import {
  IconArrowRight,
  IconBan,
  IconCases,
  IconCheck,
  IconCourier,
  IconLink,
  IconLock,
  IconMail,
  IconRefresh
} from '../components/icons';
import { PLAN_KEYS, planLabel } from '../components/plans';

const planLabels = PLAN_KEYS.reduce((acc, key) => ({ ...acc, [key]: planLabel(key) }), {});

/** Icons mirror the merchant portal, so the same decision reads the same way. */
const actions = [
  { label: 'Approve refund', icon: IconCheck, tone: 'bg-success-50 text-success-600' },
  { label: 'Approve reship', icon: IconRefresh, tone: 'bg-info-50 text-info-600' },
  { label: 'Escalate to courier', icon: IconCourier, tone: 'bg-warning-50 text-warning-600' },
  { label: 'Reject case (with reason)', icon: IconBan, tone: 'bg-danger-50 text-danger-600' }
];

const assurances = [
  { icon: IconLock, label: 'No account required' },
  { icon: IconLink, label: 'No integration' },
  { icon: IconMail, label: 'Works in your inbox' }
];

const magicLinkPoints = [
  {
    icon: IconLock,
    title: 'No account required',
    description: 'Open the secure link and act. Nothing to install, nothing to log into.'
  },
  {
    icon: IconCases,
    title: 'The complete evidence pack',
    description: 'Review the customer statement, photos, tracking history and declaration in one place.'
  },
  {
    icon: IconMail,
    title: 'Customer auto-notified',
    description: 'Your decision goes back to the customer automatically — no reply to write.'
  }
];

const MerchantsPage = () => {
  const [searchParams] = useSearchParams();
  const plan = searchParams.get('plan');
  const validPlan = plan && planLabels[plan] ? plan : null;

  return (
    <>
      {/* Hero */}
      <section className="bg-brand-900">
        <Container width="full" className="py-12 sm:py-16">
          {validPlan && (
            <Alert tone="info" title={`You selected the ${planLabels[validPlan]} plan.`} className="mb-8">
              When you receive your first delivery issue case, you&apos;ll get a secure link to choose
              your plan and pay. See{' '}
              <Link to="/pricing?for=merchants" className="font-600 underline hover:text-info-600">
                pricing
              </Link>{' '}
              for details.
            </Alert>
          )}

          <div className="grid items-center gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <div>
              <p className="text-[11px] font-700 uppercase tracking-[0.14em] text-gold-400">
                For merchants
              </p>
              <h1 className="mt-3 font-display text-[34px] font-800 leading-[1.08] tracking-tight text-white sm:text-[44px]">
                Delivery issues,
                <br />
                <span className="text-gold-400">already investigated.</span>
              </h1>
              <p className="mt-5 max-w-xl text-lg leading-relaxed text-white/80">
                No integration. No second system to learn. Act through a secure link and move on.
              </p>

              <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
                {assurances.map(({ icon: Icon, label }) => (
                  <li key={label} className="flex items-center gap-2 text-sm font-medium text-white/85">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gold-500 text-brand-900">
                      <Icon size={14} />
                    </span>
                    {label}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button as={Link} to="/pricing?for=merchants" variant="gold" size="lg">
                  See pricing
                  <IconArrowRight size={18} />
                </Button>
                <Button as={Link} to="/merchant/login" variant="onDark" size="lg">
                  Merchant sign in
                </Button>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-6 shadow-panel">
              <p className="font-display text-[15px] font-700 text-ink-900">Available actions</p>
              <p className="mt-1 text-sm text-ink-600">One decision closes the case.</p>
              <ul className="mt-5 space-y-2.5">
                {actions.map(({ label, icon: Icon, tone }) => (
                  <li
                    key={label}
                    className="flex items-center gap-3 rounded-xl border border-ink-200 px-3.5 py-3"
                  >
                    <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tone}`}>
                      <Icon size={18} />
                    </span>
                    <span className="text-sm font-600 text-ink-900">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>

      {/* Secure magic-link access */}
      <section className="bg-white py-12 sm:py-14">
        <Container width="full">
          <h2 className="font-display text-2xl font-800 tracking-tight text-ink-900 sm:text-[28px]">
            Secure magic-link access
          </h2>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-ink-600">
            Every case arrives as a read-only link with the evidence already gathered.
          </p>

          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {magicLinkPoints.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="flex items-start gap-3.5 rounded-xl border border-ink-200 bg-white p-4 shadow-card"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gold-50 text-gold-700 ring-1 ring-inset ring-gold-100">
                  <Icon size={19} />
                </span>
                <div className="min-w-0">
                  <h3 className="font-display text-[15px] font-700 text-ink-900">{title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-ink-600">{description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-start gap-3 rounded-xl bg-gold-50 px-4 py-3.5 ring-1 ring-inset ring-gold-100">
            <IconLock size={17} className="mt-0.5 shrink-0 text-gold-700" />
            <p className="text-sm leading-relaxed text-ink-700">
              <span className="font-600">Important:</span> You&apos;re not forced into a second system.
              Act through the secure link and move on.
            </p>
          </div>
        </Container>
      </section>

      {/* The same Pulse panel the home page shows, with the figures that
          speak to this audience. */}
      <PulseSection
        panels={['merchant']}
        title="Retailers in the Pulse"
        description="The retailers customers are reporting delivery issues against right now."
      />

      {/* Pricing CTA */}
      <section className="bg-ink-50 py-12 sm:py-14">
        <Container width="full">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-gold-500 px-6 py-8 sm:px-8 lg:flex-row lg:items-center">
            <div className="max-w-xl">
              <h2 className="font-display text-2xl font-700 tracking-tight text-brand-900">Pricing</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-brand-900/75">
                Start with a free pilot and see the value before you commit — then one simple
                per-user plan with unlimited cases.
              </p>
            </div>
            <Button as={Link} to="/pricing?for=merchants" size="lg" className="shrink-0">
              See pricing
              <IconArrowRight size={18} />
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
};

export default MerchantsPage;
